/**
 * RotaDashboard.jsx
 * 
 * Main hub for staff scheduling/rota management.
 * Shows current week overview, quick stats, and navigation to rota builder.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as PublishIcon,
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  ArrowForward as ForwardIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const RotaDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState(null);
  const [stats, setStats] = useState({
    current_week_published: false,
    next_week_published: false,
    shifts_this_week: 0,
    employees_working_today: 0
  });
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    return monday.toISOString().split('T')[0];
  });

  useEffect(() => {
    loadData();
  }, [currentWeekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [weekResponse, statsResponse] = await Promise.all([
        api.get(`/rota/week?week_start=${currentWeekStart}`),
        api.get('/rota/stats')
      ]);
      
      setWeekData(weekResponse.data);
      setStats(statsResponse.data);
    } catch (err) {
      console.error('Failed to load rota data:', err);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(currentWeekStart);
    current.setDate(current.getDate() + (direction * 7));
    setCurrentWeekStart(current.toISOString().split('T')[0]);
  };

  const formatWeekRange = (startDate) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    
    const options = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)}`;
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
    return currentWeekStart === monday.toISOString().split('T')[0];
  };

  const getDayName = (index) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days[index];
  };

  const getDateFromWeekStart = (weekStart, dayIndex) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    return date;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 0
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/time')}
            sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Back to Time & Attendance
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Staff Rota
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Build and manage your team's weekly schedule
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#667eea20', color: '#667eea' }}>
                    <PeopleIcon />
                  </Avatar>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="#667eea">
                  {stats.employees_working_today}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="600">
                  Working Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#4CAF5020', color: '#4CAF50' }}>
                    <ScheduleIcon />
                  </Avatar>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                  {stats.shifts_this_week}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="600">
                  Shifts This Week
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: stats.current_week_published ? '#4CAF5020' : '#FF980020', 
                    color: stats.current_week_published ? '#4CAF50' : '#FF9800' 
                  }}>
                    <PublishIcon />
                  </Avatar>
                </Box>
                <Typography variant="h4" fontWeight="bold" color={stats.current_week_published ? '#4CAF50' : '#FF9800'}>
                  {stats.current_week_published ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="600">
                  This Week Published
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: stats.next_week_published ? '#4CAF5020' : '#9E9E9E20', 
                    color: stats.next_week_published ? '#4CAF50' : '#9E9E9E' 
                  }}>
                    <CalendarIcon />
                  </Avatar>
                </Box>
                <Typography variant="h4" fontWeight="bold" color={stats.next_week_published ? '#4CAF50' : '#9E9E9E'}>
                  {stats.next_week_published ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight="600">
                  Next Week Published
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Week Navigation */}
        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigateWeek(-1)}>
                  <BackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Week of {formatWeekRange(currentWeekStart)}
                  </Typography>
                  {isCurrentWeek() && (
                    <Chip label="Current Week" size="small" color="primary" sx={{ mt: 0.5 }} />
                  )}
                </Box>
                <IconButton onClick={() => navigateWeek(1)}>
                  <ForwardIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={() => navigate(`/rota/builder?week=${currentWeekStart}&copy=true`)}
                >
                  Copy from Previous
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/rota/builder?week=${currentWeekStart}`)}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    '&:hover': { opacity: 0.9 }
                  }}
                >
                  Edit Rota
                </Button>
              </Box>
            </Box>

            {/* Week Overview Grid */}
            {weekData && (
              <Box>
                <Grid container spacing={1}>
                  {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                    const dateObj = getDateFromWeekStart(currentWeekStart, dayIndex);
                    const dateStr = dateObj.toISOString().split('T')[0];
                    const coverage = weekData.coverage?.[dateStr] || {};
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    const hasWarning = coverage.unassigned > 2;

                    return (
                      <Grid item xs={12} sm={6} md={12/7} key={dayIndex}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            bgcolor: isToday ? '#667eea10' : 'white',
                            border: isToday ? '2px solid #667eea' : '1px solid #e2e8f0',
                            borderRadius: 2
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color={isToday ? '#667eea' : 'text.primary'}>
                            {getDayName(dayIndex)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dateObj.getDate()}/{dateObj.getMonth() + 1}
                          </Typography>
                          
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="h5" fontWeight="bold" color="#4CAF50">
                              {coverage.working || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              working
                            </Typography>
                          </Box>

                          {coverage.on_holiday > 0 && (
                            <Chip
                              label={`${coverage.on_holiday} off`}
                              size="small"
                              sx={{ mt: 1, bgcolor: '#FF980020', color: '#FF9800', fontSize: '0.7rem' }}
                            />
                          )}

                          {hasWarning && (
                            <Tooltip title={`${coverage.unassigned} employees not assigned`}>
                              <WarningIcon sx={{ mt: 1, color: '#FF9800', fontSize: 18 }} />
                            </Tooltip>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Employee Summary */}
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Team ({weekData.total_employees} employees)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {weekData.employees?.slice(0, 10).map((emp) => (
                      <Chip
                        key={emp.id}
                        label={emp.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: emp.holidays?.length > 0 ? '#FF9800' : '#e2e8f0',
                          color: emp.holidays?.length > 0 ? '#FF9800' : 'text.primary'
                        }}
                      />
                    ))}
                    {weekData.employees?.length > 10 && (
                      <Chip label={`+${weekData.employees.length - 10} more`} size="small" />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#2c3e50' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
              }}
              onClick={() => navigate(`/rota/builder?week=${currentWeekStart}`)}
            >
              <CardContent sx={{ p: 3 }}>
                <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #667eea, #764ba2)', mb: 2 }}>
                  <EditIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Build Rota
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop shifts to create your weekly schedule.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
              }}
              onClick={() => navigate('/rota/templates')}
            >
              <CardContent sx={{ p: 3 }}>
                <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #4CAF50, #45a049)', mb: 2 }}>
                  <ScheduleIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Shift Templates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create and manage reusable shift patterns.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.1)' }
              }}
              onClick={() => navigate('/rota/my-schedule')}
            >
              <CardContent sx={{ p: 3 }}>
                <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #00bcd4, #0097a7)', mb: 2 }}>
                  <ViewIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  My Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View your upcoming shifts and work schedule.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Publish Alert */}
        {!stats.current_week_published && (
          <Alert
            severity="warning"
            sx={{ mt: 4 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/rota/builder?week=${currentWeekStart}`)}
              >
                Publish Now
              </Button>
            }
          >
            This week's rota hasn't been published yet. Your team can't see their shifts until you publish.
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default RotaDashboard;